import React, {
  useState,
  useEffect,
  useRef,
  CSSProperties,
  ReactElement,
  useCallback,
} from "react";
import { Tree, NodeRendererProps } from "react-arborist";
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Icon,
  EmptyState,
} from "@shopify/polaris";
import {
  EditIcon,
  PlusIcon,
  InfoIcon,
  StarFilledIcon,
  StarIcon,
  CartIcon,
} from "@shopify/polaris-icons";
import { ChevronDown, Folder } from "lucide-react";
import { useTranslation } from "react-i18next";
import CategoryFiles from "containers/document/category-files/categoryFiles";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  fetchCategoryDetails,
  setDestroy,
} from "containers/document/category-tree/category-tree.Slice";
import { RootState, AppDispatch } from "configureStore";
import "../categoryTree.css";
import { Routes, Route } from "react-router-dom";
import { Category, CategoryDataTbl } from "types/Document";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import useResizeObserver from "use-resize-observer";
import FilesSearch from "components/files-search/FilesSearch";
import Loading from "components/loading/Loading";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import CategoryTreeSkeleton from "containers/document/category-tree/CategoryTreeSkeleton";
import CategoryRowTouch from "containers/document/category-tree/CategoryRowTouch";
import useIsBelowLg from "containers/document/use-is-below-lg";
import { PHONE_PAGE_GUTTER } from "components/tw/pageGutter";

interface DocumentArboristProps {
  userId?: string;
  source?: string;
}

const LOCAL_STORAGE_KEY = "document-tree-state";

// The cabinet's Documents section is also open at these addresses; their last
// segment is not a category id.
const PROFILE_PAGES_WITHOUT_CATEGORY = ["downloads", "onboarding-documents"];

const DocumentArborist: React.FC<DocumentArboristProps> = ({
  userId,
  source = "",
}) => {
  const { t } = useTranslation();
  const history = useNavigate();
  const [activeNodeId, setActiveNodeId] = useState<string>("");
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>(
    {}
  );
  const { categoriesData, isLoading } = useSelector(
    (state: RootState) => state.documentCategoryReducer
  );
  const { isUserMaster } = useSelector(
    (state: RootState) => state.companySelectReducer
  );
  const isOperational = source === "document" ? true : false;
  // Inside the cabinet's Documents section the page around the tree already has
  // the title, and the file history (a Master-only report) would answer 403 to
  // everyone else, so the tree brings neither.
  const isEmbedded = source === "profile";
  // On a phone the search and the category button need a side margin of their own, except inside
  // the cabinet, whose frame already has one.
  const phoneGutter = isEmbedded ? "" : PHONE_PAGE_GUTTER;

  const { ref, height } = useResizeObserver();

  // Below lg the tree is a folded panel above the list: it opens from the
  // "Kategorija: ..." button and folds away again after a category is picked.
  const isBelowLg = useIsBelowLg();
  const [isTreeOpen, setIsTreeOpen] = useState(false);
  const filesRef = useRef<HTMLDivElement>(null);

  const dispatch: AppDispatch = useDispatch();
  const { companyId } = useParams<{ companyId: string }>();

  useEffect(() => {
    const savedState = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      setActiveNodeId(parsedState.activeNodeId || "");
      setExpandedNodes(parsedState.expandedNodes || {});
    }
  }, []);

  const loadTreeFileCategoriesData = useCallback(() => {
    if (companyId) {
      dispatch(fetchCategoryDetails({ companyId, source }));
    }
  }, [companyId, dispatch]);

  useEffect(() => {
    loadTreeFileCategoriesData();
    setActiveNodeId("");

    return () => {
      dispatch(setDestroy());
    };
  }, [companyId, loadTreeFileCategoriesData]);

  useEffect(() => {
    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({ activeNodeId, expandedNodes })
    );
  }, [activeNodeId, expandedNodes]);

  function transformData(data: Category[]): CategoryDataTbl[] {
    return data
      ?.map((item) => ({
        id: item.id,
        name: item.title,
        count: item.fileCount,
        parentId: item.parentCategory,
        children: item.subcategories ? transformData(item.subcategories) : [],
        order: item.order,
        isFavourite: item.isFavourite || false,
      }))
      .sort((a, b) => (Number(a.order) ?? 0) - (Number(b.order) ?? 0));
  }

  const transformedData: CategoryDataTbl[] = transformData(categoriesData);

  const hasTreeData =
    Array.isArray(transformedData) && transformedData.length > 0;
  // First load → show a skeleton; background refresh → show the small spinner.
  const showTreeSkeleton = isLoading && !hasTreeData;

  const getDynamicURIName = (url: string) => {
    const urlParts = url.split("/");
    const dynamicName = urlParts[urlParts.length - 1];
    return dynamicName;
  };

  let location = useLocation();
  const dynamicRoute = getDynamicURIName(location.pathname);

  useEffect(() => {
    if (
      dynamicRoute &&
      dynamicRoute !== "documents" &&
      dynamicRoute !== "files" &&
      !(isEmbedded && PROFILE_PAGES_WITHOUT_CATEGORY.includes(dynamicRoute))
    ) {
      setActiveNodeId(dynamicRoute);
      history(`${location.pathname}`);
    } else {
      setActiveNodeId("");
      setExpandedNodes({});
    }
  }, [dynamicRoute, history, location.pathname, isEmbedded]);

  const findCategoryName = (
    nodes: CategoryDataTbl[] | undefined,
    id: string
  ): string | undefined => {
    for (const node of nodes ?? []) {
      if (node.id === id) return node.name;
      const nameInChildren = findCategoryName(node.children, id);
      if (nameInChildren) return nameInChildren;
    }
    return undefined;
  };

  // Helper to find parent chain from a given nodeId
  const findParentChain = (
    nodes: CategoryDataTbl[] | undefined,
    targetId: string,
    path: string[] = []
  ): string[] | null => {
    if (!Array.isArray(nodes)) return null;

    for (const node of nodes) {
      if (node.id === targetId) {
        return [...path];
      }
      if (node.children) {
        const result = findParentChain(node.children, targetId, [
          ...path,
          node.id,
        ]);
        if (result) return result;
      }
    }
    return null;
  };

  useEffect(() => {
    if (!activeNodeId || !Array.isArray(transformedData)) return;

    const parentChain = findParentChain(transformedData, activeNodeId);
    if (parentChain) {
      const newExpanded = parentChain.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {} as Record<string, boolean>);

      const isDifferent = Object.keys(newExpanded).some(
        (key) => !expandedNodes[key]
      );

      if (isDifferent) {
        setExpandedNodes((prev) => ({ ...prev, ...newExpanded }));
      }
    }
  }, [activeNodeId, transformedData]);

  const handleClick = (node: any) => {
    setActiveNodeId(node.id);
    toggleNode(node.id);
    const basePath = userId
      ? `/app/${companyId}/profile/${userId}/files`
      : `/app/${companyId}/documents`;
    history(`${basePath}/${node.id}`);
    if (isBelowLg) {
      // The list opens below the tree: without folding the tree away the tap
      // looks like it did nothing.
      setIsTreeOpen(false);
      requestAnimationFrame(() => {
        const files = filesRef.current;
        if (!files) return;
        const { top } = files.getBoundingClientRect();
        // The page scrolls inside `main`, which starts under the top bar. On a phone the fixed bottom bar
        // covers the last part of it (the padding `main` keeps free), and a list starting there is not
        // seen: the top must leave at least 48 px of the list's head above that part.
        const main = files.closest("main");
        let isTopVisible: boolean;
        if (main) {
          const mainBox = main.getBoundingClientRect();
          const barHeight = parseFloat(getComputedStyle(main).paddingBottom);
          isTopVisible = top >= mainBox.top && top < mainBox.bottom - barHeight - 48;
        } else {
          isTopVisible = top >= 0 && top < window.innerHeight;
        }
        // Only when the list starts off screen, and at once: a smooth scroll keeps moving the page
        // for a moment, and a tap on «Kategorija» in that moment lands where the button used to be.
        if (!isTopVisible) files.scrollIntoView?.({ behavior: "auto", block: "start" });
      });
    }
  };

  // Toggle the expanded state of a node
  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  const handleToggle = () => {
    // console.log(node, "node");
  };

  const nodeRef = useRef<HTMLDivElement>(null);

  const handleCreateFilesCategory = () => {
    dispatch(setModalxComponent("AddFilesCategoryForm"));
  };

  const handleEditFilesCategory = ({ id }: { id: string }) => {
    dispatch(setModalxComponent("AddFilesCategoryForm"));
    dispatch(setModalxComponentProps({ id }));
  };

  const handleInfoFilesCategory = ({ id }: { id: string }) => {
    dispatch(setModalxComponent("InfoFilesCategory"));
    dispatch(setModalxComponentProps({ id }));
  };

  const handleAddNode = (parentId: string) => {
    dispatch(setModalxComponent("AddFilesCategoryForm"));
    dispatch(setModalxComponentProps({ parentId }));
  };

  const handleToggleFavorite = (
    e: React.MouseEvent,
    categoryId: string,
    categoryName: string,
    currentFavorite: boolean
  ) => {
    e.stopPropagation();
    dispatch(setModalxComponent("CategoryFavoriteConfirm"));
    dispatch(
      setModalxComponentProps({
        categoryId,
        categoryName,
        isFavorite: currentFavorite,
        source,
      })
    );
  };

  const handleAddFolderToCart = (
    e: React.MouseEvent,
    categoryId: string,
    categoryName: string
  ) => {
    e.stopPropagation();
    dispatch(setModalxComponent("AddFolderToCartConfirm"));
    dispatch(
      setModalxComponentProps({
        categoryId,
        categoryName,
        source: source === "profile" ? "profile" : "document",
      })
    );
  };

  const renderNode = ({
    node,
    style,
    dragHandle,
  }: NodeRendererProps<CategoryDataTbl>): ReactElement => {
    const isActive = node.id === activeNodeId;
    const isExpanded = expandedNodes[node.id] || false; // Check if node is expanded from localStorage

    // Ensure node is opened if it should be expanded
    if (isExpanded && !node.isOpen) {
      // Use requestAnimationFrame to avoid calling during render
      requestAnimationFrame(() => {
        if (node && !node.isOpen) {
          node.toggle(); // Ensure node is opened
        }
      });
    }

    if (isBelowLg) {
      return (
        <CategoryRowTouch
          node={node}
          style={style as CSSProperties}
          isActive={isActive}
          canManage={isUserMaster && isOperational}
          onSelect={() => handleClick(node)}
          onToggleOpen={() => {
            toggleNode(node.id);
            node.toggle();
          }}
          onInfo={() => handleInfoFilesCategory({ id: node.id })}
          onAddToCart={(e) => handleAddFolderToCart(e, node.id, node.data.name)}
          onToggleFavorite={(e) =>
            handleToggleFavorite(
              e,
              node.id,
              node.data.name,
              node.data.isFavourite || false
            )
          }
          onEdit={() => handleEditFilesCategory({ id: node.id })}
          onAddChild={() => handleAddNode(node.id)}
        />
      );
    }

    return (
      <div
        style={style as CSSProperties}
        ref={nodeRef}
        tabIndex={0}
        onClick={() => handleClick(node)}
        className={isActive ? "node_txt active-node" : "node_txt"}
      >
        <InlineStack gap="100">
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {node.children!.length! > 0 ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNode(node.id);
                  node.toggle();
                }}
                style={{ marginRight: "8px" }}
              >
                {node.isOpen ? "-" : "+"}
              </button>
            ) : (
              <button
                disabled
                className="disabled_btn"
                style={{ marginRight: "8px" }}
              ></button>
            )}
            <span
              style={{
                display: node.data.name.length ? "inline-block" : "none",
                whiteSpace: "nowrap",
                // overflow: "hidden",
                // textOverflow: "ellipsis",
                // maxWidth: "150px",
              }}
              title={node.data.name}
            >
              {node.data.name}
            </span>
            &nbsp;[{node.data.count <= 0 ? "x" : node.data.count}]
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleInfoFilesCategory({ id: node.id });
              }}
              style={{ marginLeft: "8px" }}
            >
              <Icon source={InfoIcon as any} tone="base" />
            </span>
            <span
              onClick={(e) =>
                handleAddFolderToCart(e, node.id, node.data.name)
              }
              style={{ marginLeft: "8px", cursor: "pointer" }}
              title={t("document_cart_add_folder")}
            >
              <Icon source={CartIcon as any} tone="base" />
            </span>
            {isUserMaster && isOperational && (
              <span
                onClick={(e) => {
                  handleToggleFavorite(
                    e,
                    node.id,
                    node.data.name,
                    node.data.isFavourite || false
                  );
                }}
                style={{ marginLeft: "8px", cursor: "pointer" }}
                title={
                  node.data.isFavourite
                    ? t("remove_from_favorites")
                    : t("add_to_favorites")
                }
              >
                <Icon
                  source={
                    (node.data.isFavourite ? StarFilledIcon : StarIcon) as any
                  }
                  tone={node.data.isFavourite ? "critical" : "base"}
                />
              </span>
            )}
            {isUserMaster && isOperational && (
              <>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditFilesCategory({ id: node.id });
                  }}
                  style={{ marginLeft: "8px" }}
                >
                  <Icon source={EditIcon as any} tone="base" />
                </span>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddNode(node.id);
                  }}
                  style={{ marginLeft: "8px" }}
                >
                  <Icon source={PlusIcon as any} tone="base" />
                </span>
              </>
            )}
          </span>
        </InlineStack>
      </div>
    );
  };

  // With no category picked there is nothing else to show, so the tree is open.
  const isTreePanelShown = isTreeOpen || !activeNodeId;
  const activeCategoryName = findCategoryName(transformedData, activeNodeId);

  return (
    <Page 
      fullWidth
      title={isEmbedded ? undefined : t("documents")}
      titleMetadata={
        isEmbedded ? undefined : (
          <AuditLogPopover 
            companyId={companyId!} 
            // Show both file and category activity on the main page
            subjectType="File,FileCategory"
            label={t("history", { defaultValue: "History" })}
          />
        )
      }
    >
      {/* Wide screens: the tree on the left, search and list on the right.
          Below lg everything stacks as search, category, list, because the tree
          beside the list leaves both too narrow. */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[18rem_minmax(0,1fr)] lg:grid-rows-[auto_1fr] xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <div className="min-w-0 max-lg:order-2 lg:row-span-2">
          {activeNodeId && (
            <div className={`lg:hidden ${phoneGutter}`}>
              <button
                type="button"
                className="mb-3 inline-flex min-h-11 min-w-0 max-w-full items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900"
                aria-expanded={isTreePanelShown}
                onClick={() => setIsTreeOpen((open) => !open)}
              >
                <Folder
                  className="h-4 w-4 shrink-0 text-gray-500"
                  aria-hidden="true"
                />
                <span className="shrink-0 text-gray-500">{t("category")}:</span>
                <span className="truncate font-medium">{activeCategoryName}</span>
                <ChevronDown
                  className="h-4 w-4 shrink-0 text-gray-500"
                  aria-hidden="true"
                />
              </button>
            </div>
          )}
          {/* Folded away by collapsing, not by display:none: a tree inside display:none is measured as 0 high
              and would open as an empty card. Collapsed, it keeps its 50dvh and its measured height. */}
          <div
            className={
              isTreePanelShown
                ? undefined
                : "max-lg:invisible max-lg:h-0 max-lg:overflow-hidden"
            }
            data-testid="category-tree-panel"
          >
            <BlockStack gap="500">
              <Card roundedAbove="sm">
                <InlineStack align="space-between" blockAlign="center">
                  <InlineStack align="start" gap="100">
                    <Text as="h2" variant="headingSm">
                      {t("category")}
                    </Text>
                    {isLoading && hasTreeData && <Loading size="small" />}
                  </InlineStack>
                  {isUserMaster && isOperational && (
                    <Button onClick={handleCreateFilesCategory}>
                      {t("add")}
                    </Button>
                  )}
                </InlineStack>
                <div
                  className="arborist-container max-lg:!h-[50dvh]"
                  ref={ref}
                >
                  {showTreeSkeleton ? (
                    <CategoryTreeSkeleton rows={10} />
                  ) : (
                    <Tree
                      data={transformedData}
                      onToggle={handleToggle}
                      disableMultiSelection
                      idAccessor="id"
                      openByDefault={false}
                      rowHeight={isBelowLg ? 44 : 30}
                      height={height}
                      // width={width}
                    >
                      {renderNode}
                    </Tree>
                  )}
                </div>
              </Card>
            </BlockStack>
          </div>
        </div>
        <div className={`min-w-0 max-lg:order-1 ${phoneGutter}`}>
          <FilesSearch source={source} />
        </div>
        <div className="min-w-0 max-lg:order-3" ref={filesRef}>
          {activeNodeId ? (
            <BlockStack gap="500">
              <Card roundedAbove="sm">
                <Routes>
                  <Route
                    path=":id"
                    element={
                      <CategoryFiles id={activeNodeId} source={source} />
                    }
                  />
                </Routes>
              </Card>
            </BlockStack>
          ) : (
            <div className="max-md:hidden">
              <BlockStack gap="500">
                <Card roundedAbove="sm">
                  <EmptyState
                    // heading={t('no_categories')}
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  ></EmptyState>
                </Card>
              </BlockStack>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
};

export default DocumentArborist;
